import getConfig from 'next/config';
import is from 'is_js';
import {
  setLocaleData, setNewsData, setProducts, setExchangeInfo, setMetaData,
} from '../actions/load';
import { setTheme } from '../actions/theme';
import makeApiRequest from '../utils/makeApiRequest';
import makeOrCacheApiReq from '../utils/apiCache';
import { getHostName } from '../utils/urlUtils';
import bog from '../utils/moreBog';
import { readCookie } from '../utils/cookies-browser';
import metaInfo from '../config/metaInfo';
import { gwGet } from '../actions/actionsFactory/gwRequest';
import API_PATH from '../actions/actionsFactory/apiPath';
import { handleGwData } from '../actions/actionsFactory';
import { THEME_LIGHT } from '../reducers/theme';
import { fetchNotice } from '../actions/actionsSet/market';

import { setPublicAssetsStatus, hideHeaderLangSelect } from '../actions/actionsSet/userConfig';
import {
  handleMarginCoeffTask,
  handleMarginSymbolsTask,
  wssUri,
} from './handleInitTask';
import { setMarginStopLimitOrOCOTab } from '../actions/actionsSet/margin';

export const PAIR_WHITE_LIST = [
  { pair: 'BCCBTC', baseName: 'Bitcoin Cash' },
  { pair: 'BCCBNB', baseName: 'Bitcoin Cash' },
  { pair: 'BCCETH', baseName: 'Bitcoin Cash' },
  { pair: 'BCCUSDT', baseName: 'Bitcoin Cash' },
  { pair: 'USDCBNB', baseName: 'USD Coin' },
  { pair: 'USDCBTC', baseName: 'USD Coin' },
];
const MARGIN = 'MARGIN';
const EXCHANGE = 'EXCHANGE';
export const TYPE_ENUMS = {
  [MARGIN]: 'MARGIN',
  [EXCHANGE]: 'EXCHANGE',
};

const {
  publicRuntimeConfig: {
    routePrefix: ROUTE_PREFIX,
    enableMargin,
  },
} = getConfig();

const { brands, metaKeywords, metaDescriptions } = metaInfo;

const INFO_API_PATH = '/api/v1/exchangeInfo';
const I18N_API_PATH = locale => `${ROUTE_PREFIX}/en/i18n/${locale}.json`;

// !!! only ONE `await` allowed in this code !!!

export default async ({
  isServer, req, query, store,
}) => {
  let theme = isServer ? req.cookies.theme : readCookie('theme');
  if (!theme) {
    theme = THEME_LIGHT;
  }
  store.dispatch(setTheme(theme));

  const shouldPublicAssets = isServer ? req.cookies.shouldPublicAssets : readCookie('shouldPublicAssets');
  store.dispatch(setPublicAssetsStatus(shouldPublicAssets === 'true'));

  const isHideHeadLangSeclet = isServer ? req.cookies.isHideHeadLangSeclet : readCookie('isHideHeadLangSeclet');
  store.dispatch(hideHeaderLangSelect(isHideHeadLangSeclet === 'true'));

  const marginStopLimitOrOCOTab = isServer ? req.cookies.marginStopLimitOrOCOTab : readCookie('marginStopLimitOrOCOTab');
  if (marginStopLimitOrOCOTab === 'true') {
    store.dispatch(setMarginStopLimitOrOCOTab(marginStopLimitOrOCOTab));
  }

  const [ba, qa] = query.symbol?.toUpperCase().split('_');

  const pair = `${ba} / ${qa}`.toUpperCase();
  const symbol = `${ba}${qa}`.toUpperCase();

  const { type } = query;
  if (!pair.includes('/') || (type && !TYPE_ENUMS[type.toUpperCase()])) {
    bog.warn('Invalid market pair', pair);
    return { render404Error: true }; // the minimum
  }


  const state = store.getState();

  const isInWhiteList = PAIR_WHITE_LIST.find(p => p.pair === symbol);
  if (is.empty(state.tickers)) {
    let productData = await makeOrCacheApiReq(
      API_PATH.PRODUCTS_API_PATH,
      () => gwGet(API_PATH.PRODUCTS_API_PATH, null, req, false),
      res => res.success && Array.isArray(res.data),
    );
    productData = handleGwData(productData);
    const currentSymbol = productData && productData.find(s => s.s === symbol && s.b === ba);
    if (isInWhiteList || currentSymbol) {
      store.dispatch(setProducts(productData));
    } else {
      return { render404Error: true };
    }
  } else if (!isInWhiteList && !state.tickers[symbol]) {
    return { render404Error: true };
  }

  const currencyInfoBaseUrl = `${API_PATH.ASSET_INFO_API}?name=`;

  // only ONE `await` allowed in this code
  const promises = []; // do requests ASYNC
  const locale = (query || {}).locale || 'en';

  const titlePair = `${ba}${qa}`;
  const both = [ba, qa];

  // base props
  const props = {
    metaData: {
      title: brands[locale] || brands.en,
      keywords: metaKeywords[locale] || metaKeywords.en || '',
      description: metaDescriptions[locale] || metaDescriptions.en || '',
    },
    symbol,
    base: ba,
    both,
    locale,
    wssUri: wssUri(req),
    hostName: getHostName(req),
  };

  // 1
  is.empty(state.info) &&
  promises.unshift(
    makeOrCacheApiReq(
      INFO_API_PATH,
      () => makeApiRequest(INFO_API_PATH, req),
    ),
  );

  let localeData = store.getState().localeData[locale];
  let news = store.getState().news[locale];

  // 2
  if (!localeData) {
    promises.unshift(
      makeOrCacheApiReq(
        `beta-i18n-${locale}`,
        () => makeApiRequest(I18N_API_PATH(locale), req, null, null, true),
      ),
    ); // 7
  }

  // 3
  // uses locale, cache key = NEWS_API_PATH + locale
  if (!news) {
    promises.unshift(fetchNotice(req, locale)); // 4
  }

  // 4
  if (enableMargin && is.empty(state.marginSymbols)) promises.unshift(handleMarginSymbolsTask(store, req, both));

  // 5
  if (enableMargin && is.empty(state.marginCoeff)) promises.unshift(handleMarginCoeffTask(store, req));

  // fetch data in parallel
  const responses = await Promise.all(promises);

  // 1
  if (is.empty(state.info)) store.dispatch(setExchangeInfo(responses.pop())); // 1

  // 2
  if (!localeData) localeData = responses.pop();
  store.dispatch(setLocaleData(locale, localeData));

  // 3
  if (!news) {
    news = responses.pop();
    store.dispatch(setNewsData(locale, news));
  }

  // 6 fetch Binance Info. NOT CONCURRENT because we needed the base asset name from tickers.
  let binanceInfoCurrency = [];
  let baseName;
  const pairInWhiteList = PAIR_WHITE_LIST.find(p => p.pair === symbol);
  const whiteListBaseName = pairInWhiteList && pairInWhiteList.baseName;
  try {
    baseName = store.getState().tickers[symbol]
      ? store.getState().tickers[symbol].bn
      : whiteListBaseName;
    const fullInfoApiPath = currencyInfoBaseUrl + baseName;
    binanceInfoCurrency = await makeOrCacheApiReq(
      fullInfoApiPath,
      () => makeApiRequest(fullInfoApiPath, req),
    );
  } catch (err) {
    bog.warn('Binance Info request failed', err);
  }

  // 7
  const metaTitle = `${titlePair} | ${localeData.Buy || 'Buy'} ${baseName} | ${localeData.Binance ||
  brands[locale] || 'Binance'}`;

  const metaData = {
    title: metaTitle,
    keywords: metaKeywords[locale] || metaKeywords.en || '',
    description: metaDescriptions[locale] || metaDescriptions.en || '',
  };
  store.dispatch(setMetaData(metaData));

  // JUST data for this page, not shared
  return {
    ...props,
    metaData,
    binanceInfoCurrency,
  };
};
